<template>
  <div class="kpt-group-entities-component">
    <div class="kpt-group-entities-table">
      <b-table
        :data="normalizedEntities"
        sticky-header
        paginated
        :per-page="pageSize"
        :current-page.sync="currentPage"
        :narrowed="true"
        :hoverable="true"
        sort-icon="chevron-up"
        sort-icon-size="is-small"
        ref="groupTable"
        class="has-horizontal-scroll is-full-height">
        <b-table-column v-for="(column, idx) in entityColumns" :key="idx"
          :field="column.field" 
          :label="column.label" 
          v-slot="props" 
          sortable>
          <span v-if="column.isHyperlink">
            <router-link :to="{ name: `${groupEntityType}Overview`, params: { id: props.row.id } }">{{ parseColumnValue(column, props.row) || '-' }}</router-link>
          </span>
          <span v-else>
            {{ parseColumnValue(column, props.row) || '-' }}
          </span>
        </b-table-column>
        <template slot="empty">
          <p>No entities were found.</p>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
/* eslint-disable object-curly-newline */
import { orderBy } from 'lodash';
import { format } from 'date-fns';
import { idToNameParserMixin } from '../../shared/mixins/idToNameParser';
import { getModelsForTypeMixin } from '../../shared/mixins/getModelForDeviceTypeMixin';

export default {
  data() {
    return {
      currentPage: 1,
      pageSize: 30,
      format
    }
  },
  props: {
    entities: {
      type: Array,
      default: () => []
    },
    groupEntityType: {
      type: String,
      default: null
    }
  },
  mixins: [idToNameParserMixin, getModelsForTypeMixin],
  computed: {
    entityColumns() {
      const columns = [];
      if (!this.groupEntityType) return columns;
      
      // determine which columns to present based on group entity type
      switch (this.groupEntityType) {
        case 'remote':
          columns.push({ field: 'name', label: 'Name', isHyperlink: true });
          columns.push({ field: 'accountId', label: 'Account', type: 'account' });
          columns.push({ field: 'modemSerial', label: 'Modem Serial' });
          columns.push({ field: 'modemModel', label: 'Modem Model', type: 'deviceModel' });
          break;
        case 'device':
          columns.push({ field: 'deviceTypeName', label: 'Type' });
          columns.push({ field: 'serial', label: 'Serial', isHyperlink: true });
          columns.push({ field: 'deviceModelName', label: 'Model' });
          columns.push({ field: 'accountId', label: 'Account', type: 'account' });
          break;
        case 'subscription':
          columns.push({ field: 'kymetaProductCode', label: 'Product Code' });
          columns.push({ field: 'displayId', label: 'Friendly Id', isHyperlink: true });
          columns.push({ field: 'accountId', label: 'Account', type: 'account' });
          columns.push({ field: 'startDate', label: 'Start Date', type: 'date' });
          columns.push({ field: 'endDate', label: 'End Date', type: 'date' });
          columns.push({ field: 'productName', label: 'Product' });
          columns.push({ field: 'dataPackageName', label: 'Data Package' });
          break;
        default:
          return [];
      }
      
      return columns;
    },
    normalizedEntities() {
      let normalizedEntities = [];
      if (!this.entities) return normalizedEntities;
      
      // clone entities prop
      normalizedEntities = [...this.entities];

      if (this.groupEntityType == 'remote') {
        // eslint-disable-next-line no-case-declarations
        normalizedEntities = normalizedEntities.map(r => {
          // destructure to flatten object (with modem values)
          const { 
            id,
            name, 
            accountId, 
            devices: { modemSerial, modemModel } = {} // default value if `devices` is not present
          } = r;
          return {
            id,
            name,
            accountId,
            modemSerial,
            modemModel
          };
        });
      }
      
      return normalizedEntities;
    },
    entitiesCount() {
      return this.entities ? this.entities.length : 0;
    },
    accounts() { 
      return orderBy(this.$store.state.accounts.accounts, ['name'], ['asc']);
    }
  },
  methods: {
    changePage(page) {
      this.currentPage = page;
    },
    getAccountNameFromId(accountId) {
      if (!accountId || !this.accounts) return null;
      const accountMatch = this.accounts.find(a => a.id == accountId);
      return accountMatch ? accountMatch.name : null;
    },
    parseColumnValue(column, row) {
      // extract display values based on column metadata
      switch (column.type) {
        case 'account':
          return this.getAccountNameFromId(row[column.field]);
        case 'deviceModel':
          return this.idToName(row[column.field], this.getModelsForType('DEV_MODEM', this.$store))
        case 'date':
          return this.format(new Date(row[column.field]), 'yyyy-MM-dd HH:mm:ss');
        default:
          // no special parsing needed, just return the value
          return row[column.field];
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '../../styles/_colors';

  .kpt-group-entities-component {
    .kpt-group-entities {
      flex: 1 0 auto;
      height: calc(100% - 195px);
      overflow: auto;
    }
    .kpt-group-entities-footer {
      position: absolute;
      bottom: 0px;
      left: 0px;
      width: 100%;
      border-top: 1px solid $grey-lighter;
      padding: 12px;
    }
  }
</style>
